import { Fragment, useEffect, useState } from "react";
import { FormGroup, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { useContext } from "react";
import { LocaleContext } from "../../../../routing/LangRouter";

//import redux store
import { saveTicketSelectedFilters } from "../../../../store/actions";

//import constants
import { BREAKPOINT, IS_MOBILE, WIDTH } from "../../../../constants/breakpoints";

//import general components
import BaseFilter from "../../../common/base-filter/base-filter";
import BaseCheckbox from "../../../common/base-checkbox/base-checkbox";
import { BaseButton } from "../../../common/base-button/base-button";

//import view components
import FilterRangeSlider from "../../../common/base-filter/filter-range-slider";

function IntFlightFilter({ dispatch, filterList, usedFilters, open, setOpen, setRequestFlag, setPage, setUid }) {
    //general variables
    const { t } = useTranslation();
    const { locale } = useContext(LocaleContext);

    //category names for filters
    const categoriesName = {
        prices: t("price"),
        airlines: t("departureFlightAirline"),
        return_airlines: t("returnFlightAirline"),
        airports: t("departureFlightAirport"),
        return_airports: t("returnFlightAirport"),
        departure_times: t("departureFlightTime"),
        return_departure_times: t("returnFlightTime"),
        flight_durations: t("departureFlightDuration"),
        return_flight_durations: t("returnFlightDuration"),
        arrival_times: t("Flight.Departure Flight Arrival Time"),
        return_arrival_times: t("Flight.Return Flight Arrival Time"),
        baggages: t("Flight.Departure Flight Baggage Allowance"),
        return_baggages: t("Flight.Return Flight Baggage Allowance"),
        flight_stops: t("Flight.Departure Flight Layovers"),
        return_flight_stops: t("Flight.Return Flight Layovers"),
    };

    //define categories for range filters and option filters
    const rangeFilterCategories = [
        "prices",
        "departure_times",
        "return_departure_times",
        "flight_durations",
        "return_flight_durations",
        "arrival_times",
        "return_arrival_times",
    ];
    const optionFilterCategories = [
        "airlines",
        "return_airlines",
        "airports",
        "return_airports",
        "baggages",
        "return_baggages",
        "flight_stops",
        "return_flight_stops",
    ];

    //handle changes in filter selection
    const [selectedFilters, setSelectedFilters] = useState({});
    const activeFilterCount = Object.keys(selectedFilters).length || "";

    //option filters//
    const handleOptionChange = (event, category) => {
        setPage(1);
        const { name, checked } = event.target;

        const updatedFilters = {
            ...selectedFilters,
            [category]: { ...selectedFilters?.[category], [name]: checked },
        };
        setSelectedFilters(updatedFilters);
    };

    //range slider filters//
    //define variables for range slider filters
    const [rangeValues, setRangeValues] = useState(
        rangeFilterCategories.reduce((map, obj) => {
            map[obj] = [0, filterList?.[obj]?.length - 1];
            return map;
        }, {}),
    );

    //handle change to the range slider for each filter
    const handleRangeChange = (event, newValue, filterName) => {
        setRangeValues(values => ({ ...values, [filterName]: newValue }));
    };

    //handle change to the range slider on mouse up and send the filtes to the parent component
    const handleRangeCommited = (event, _, filterName, from, to) => {
        setPage(1);
        const updatedFilters = { ...selectedFilters, [filterName]: [from, to] };
        setSelectedFilters(updatedFilters);
    };

    //this function handles the submission of filters on a mobile device.
    const handleSubmitFilter = () => {
        if (selectedFilters) {
            setPage(1);
            dispatch(saveTicketSelectedFilters(formatFiltersForSubmission(selectedFilters)));
            setRequestFlag(true);
        }
        setOpen(false);
    };

    // This function formats the selected filters for submission by converting the input object into a result object
    // where each category contains a comma-separated string of selected keys
    const formatFiltersForSubmission = input => {
        return Object.entries(input).reduce((result, [category, value]) => {
            if (optionFilterCategories.includes(category) && !!value) {
                const selectedKeys = Object?.keys(value)?.filter(key => value[key]);

                if (selectedKeys.length) {
                    result[category] = selectedKeys;
                }
            } else {
                result[category] = value;
            }
            return result;
        }, {});
    };

    //set default values for selected filters based on the provided defaultValue prop
    useEffect(() => {
        if (!usedFilters) return;

        const updatedFilters = Object.entries(usedFilters).reduce((acc, [filterName, filterValues]) => {
            if (rangeFilterCategories.includes(filterName) && !!filterValues?.[0] && !!filterValues?.[1]) {
                setRangeValues(values => ({
                    ...values,
                    [filterName]: filterValues.map(value => filterList?.[filterName]?.findIndex(item => item === value)),
                }));
                acc[filterName] = filterValues;
            }

            if (optionFilterCategories.includes(filterName) && !!filterValues?.length) {
                acc[filterName] = filterValues?.reduce((map, airline) => ({ ...map, [airline]: true }), {});
            }

            return acc;
        }, {});

        setSelectedFilters(updatedFilters);
    }, [usedFilters]);

    //handle filter cancellation
    const resetFilters = () => {
        setPage(1);
        dispatch(saveTicketSelectedFilters(null));
        setRequestFlag(true);
        setOpen(false);
    };

    return (
        <>
            <BaseFilter
                open={open}
                setOpen={setOpen}
                handleSubmitFilter={handleSubmitFilter}
                resetFilters={resetFilters}
                activeFilterCount={activeFilterCount}
                top="0"
            >
                {!!filterList &&
                    Object.keys(filterList).map((filterKey, index) => {
                        return (
                            <Fragment key={index}>
                                {filterList?.[filterKey]?.length ? (
                                    <Accordion
                                        className="base-filter__accordion"
                                        defaultExpanded={WIDTH > BREAKPOINT ? true : false}
                                        sx={{ boxShadow: "0px 0px 3px 0px rgba(0, 0, 0, 0.25)" }}
                                    >
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
                                            {categoriesName[filterKey]}
                                        </AccordionSummary>

                                        <AccordionDetails>
                                            {rangeFilterCategories.includes(filterKey) && (
                                                <FilterRangeSlider
                                                    filterList={filterList}
                                                    filterName={filterKey}
                                                    min={0}
                                                    max={(filterList?.[filterKey]?.length ?? 0) - 1}
                                                    value={rangeValues[filterKey]}
                                                    handleChange={handleRangeChange}
                                                    handleChangeCommited={handleRangeCommited}
                                                />
                                            )}

                                            {optionFilterCategories.includes(filterKey) && (
                                                <FormGroup aria-label="position" column="true" className="base-filter__filter-label">
                                                    {filterList?.[filterKey]?.map(filterOption => (
                                                        <BaseCheckbox
                                                            key={`${filterKey}-${filterOption?.code}`}
                                                            label={
                                                                filterOption?.english && filterOption?.persian
                                                                    ? locale === "en"
                                                                        ? filterOption.english
                                                                        : filterOption.persian
                                                                    : filterOption?.name || filterOption?.persian
                                                            }
                                                            name={filterOption?.code?.toString()}
                                                            checked={selectedFilters?.[filterKey]?.[filterOption?.code]}
                                                            handleChange={handleOptionChange}
                                                            otherName={filterKey}
                                                        />
                                                    ))}
                                                </FormGroup>
                                            )}
                                        </AccordionDetails>
                                    </Accordion>
                                ) : null}
                            </Fragment>
                        );
                    })}
            </BaseFilter>
            {!IS_MOBILE && !!filterList && (
                <BaseButton text={t("General.Apply Filter")} color="primary" size="medium" radius="radius-10" handleClick={handleSubmitFilter} />
            )}
        </>
    );
}

export default connect(mapStateToProps)(IntFlightFilter);

function mapStateToProps(state) {
    return {
        filterList: state.intFlightData?.results?.filters,
        usedFilters: state.intFlightData?.results?.used_filters,
    };
}
